import { fetchAuthSession } from "aws-amplify/auth";
import FormText from "components/form/FormText";
import IndicatorButton from "components/indicator/IndicatorButton";
import { AppConst } from "constants/appConst";
import { DivConst } from "constants/divConst";
import { AppHelper } from "helpers/appHelper";
import { useApiState } from "hooks/useApiState";
import { useAppState } from "hooks/useAppState";
import { useEffect, useState } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useRecoilState } from "recoil";
import { AcctState } from "stores/atoms/acctState";

/**
 *　招待受付ユーザー情報入力画面
 */
export default function InvitationUser(): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */
  type FormStateType = {
    userName: string;
  };

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();
  const { invitationCd } = useParams();

  const [acctState] = useRecoilState(AcctState);

  const { initializeAcctState, isSignedIn } = useAppState();
  const { postInvitationRef, postInvitationAccept } = useApiState();

  const [processing, setProcessing] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormStateType>();

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */
  useEffect(() => {
    (async () => {
      try {
        // 未ログインの場合は、エラー画面に遷移
        if (!(await isSignedIn())) {
          throw Error();
        }
      } catch (e) {
        showBoundary(e);
      }
    })();
  }, []);

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   *　登録ボタン押下時の処理
   */
  const onAccept = () =>
    AppHelper.submit(
      processing,
      setProcessing,
      handleSubmit(async (data: FormStateType) => {
        try {
          // 招待情報の取得
          const responseInvitation = await postInvitationRef({
            invitationCd: invitationCd,
          });
          // 招待情報が存在しない場合はエラー画面に遷移
          if (responseInvitation.member == null) {
            throw Error();
          }
          // 招待されたメールアドレスとログイン中のメールアドレスが異なる場合はエラー画面に遷移
          if (
            acctState.user &&
            responseInvitation.member.invitationMailAddress !==
              acctState.user.mailAddress
          ) {
            throw Error();
          }
          // Cognito認証情報の取得
          const session = await fetchAuthSession();

          // 招待受付APIの呼び出し
          const response = await postInvitationAccept({
            invitationCd: invitationCd,
            user: {
              accessToken: session.tokens.accessToken.toString(),
              name: data.userName,
              loginRouteDiv: DivConst.items.loginRoute.mail.value,
            },
          });
          await initializeAcctState(response.companyCd);
          navigate("/");
        } catch (e) {
          showBoundary(e);
        }
      })
    );

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <section className="section-login">
        <div className="container">
          <div className="card card-login">
            <div className="card-body">
              <h1 className="card-title">アカウント登録</h1>
              <p className="lead">アカウントを登録してください。</p>

              <form className="form-login">
                <div>
                  <FormText
                    subject="氏名"
                    name="userName"
                    register={register}
                    errors={errors}
                    required={true}
                    maxLength={100}
                    requiredMark={true}
                  />
                </div>
                <div></div>
                <div className="form-footer mt-4">
                  <button
                    type="button"
                    className="btn btn-submit w-100"
                    onClick={onAccept}
                    disabled={processing}
                  >
                    <IndicatorButton visible={processing} />
                    BranQをはじめる
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className="text-center">
            <a href={AppConst.url.privacy} target="_blank" className="caption">
              privacy &amp; Terms
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
